/**
 * Optimierte mathematische Berechnungen für Portfolio-3Topf
 *
 * Dieses Modul enthält hochoptimierte mathematische Algorithmen für statistische Auswertungen.
 *
 * Performance-Verbesserungen:
 * - Batch-Statistiken: Reduziert mehrfache Array-Sortierung
 * - Memory-effiziente Algorithmen für große Monte Carlo Simulationen
 *
 * @author Portfolio-3Topf Team
 * @version 1.1.0 (tote Funktionen entfernt)
 * @since 2.1.0
 */

/**
 * Ergebnis-Interface für umfassende statistische Berechnungen
 *
 * Enthält alle relevanten statistischen Maße, die in einem einzigen
 * Durchgang berechnet werden für maximale Effizienz.
 */
export interface BatchStatistics {
  /** Arithmetisches Mittel */
  mean: number;

  /** Median (50. Perzentil) */
  median: number;

  /** Standardabweichung */
  stdDev: number;

  /** Varianz */
  variance: number;

  /** Minimum */
  min: number;

  /** Maximum */
  max: number;

  /** 1. Perzentil (linker Tail, 1% VaR) */
  p1: number;

  /** 5. Perzentil (linker Tail, 5% VaR) */
  p5: number;

  /** 10. Perzentil */
  p10: number;

  /** 25. Perzentil (Q1) */
  p25: number;

  /** 75. Perzentil (Q3) */
  p75: number;

  /** 90. Perzentil */
  p90: number;

  /** 95. Perzentil */
  p95: number;

  /** 99. Perzentil */
  p99: number;

  /** Anzahl der Datenpunkte */
  count: number;

  /** Schiefe (Skewness) der Verteilung */
  skewness: number;

  /** Kurtosis (Wölbung) der Verteilung */
  kurtosis: number;
}

/**
 * Berechnet alle statistischen Maße in einem einzigen Durchgang
 *
 * Diese hochoptimierte Funktion reduziert die Komplexität von mehreren separaten
 * Berechnungen auf eine einzige Array-Sortierung plus lineare Durchgänge.
 *
 * Performance-Verbesserung:
 * - Vorher: O(n log n) × Anzahl_Metriken (mehrfache Sortierung)
 * - Nachher: O(n log n) + O(n) (einmalige Sortierung + lineare Berechnungen)
 *
 * @param values - Array von numerischen Werten zur Analyse
 * @returns Vollständige BatchStatistics mit allen relevanten Metriken
 *
 * @example
 * ```typescript
 * const portfolioReturns = [-5.2, 12.8, 8.1, -2.3, 15.7, 3.4, -8.9, 22.1];
 * const stats = calculateAllStatistics(portfolioReturns);
 *
 * console.log(`Durchschnittliche Rendite: ${stats.mean.toFixed(2)}%`);
 * console.log(`Risiko (Std.Abw.): ${stats.stdDev.toFixed(2)}%`);
 * console.log(`Worst Case (5. Perzentil): ${stats.p10.toFixed(2)}%`);
 * ```
 */
export const calculateAllStatistics = (values: number[]): BatchStatistics => {
  const n = values.length;

  // Edge Case: Leeres Array
  if (n === 0) {
    const emptyResult: BatchStatistics = {
      mean: 0,
      median: 0,
      stdDev: 0,
      variance: 0,
      min: 0,
      max: 0,
      p1: 0,
      p5: 0,
      p10: 0,
      p25: 0,
      p75: 0,
      p90: 0,
      p95: 0,
      p99: 0,
      count: 0,
      skewness: 0,
      kurtosis: 0,
    };
    return emptyResult;
  }

  // Edge Case: Einzelner Wert
  if (n === 1) {
    const singleValue = values[0];
    return {
      mean: singleValue,
      median: singleValue,
      stdDev: 0,
      variance: 0,
      min: singleValue,
      max: singleValue,
      p1: singleValue,
      p5: singleValue,
      p10: singleValue,
      p25: singleValue,
      p75: singleValue,
      p90: singleValue,
      p95: singleValue,
      p99: singleValue,
      count: 1,
      skewness: 0,
      kurtosis: 0,
    };
  }

  // SCHRITT 1: Einmalige Sortierung für alle Perzentil-Berechnungen
  const sorted = [...values].sort((a, b) => a - b);

  // SCHRITT 2: Mittelwert in linearer Zeit O(n)
  const sum = values.reduce((acc, val) => acc + val, 0);
  const mean = sum / n;

  // SCHRITT 3: Perzentile durch Index-Berechnung O(1) für jeden Wert
  const getPercentile = (p: number): number => {
    const index = (p / 100) * (n - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    if (lower === upper) {return sorted[lower];}
    return sorted[lower] * (1 - weight) + sorted[upper] * weight;
  };

  // SCHRITT 4: Varianz, Standardabweichung und Momente in einem Durchgang O(n)
  let variance = 0;
  let m3 = 0; // Drittes zentrales Moment (für Schiefe)
  let m4 = 0; // Viertes zentrales Moment (für Kurtosis)

  for (const value of values) {
    const deviation = value - mean;
    const deviation2 = deviation * deviation;
    const deviation3 = deviation2 * deviation;
    const deviation4 = deviation3 * deviation;

    variance += deviation2;
    m3 += deviation3;
    m4 += deviation4;
  }

  variance /= n - 1; // Stichproben-Varianz (Bessel-Korrektur)
  const stdDev = Math.sqrt(variance);

  // SCHRITT 5: Schiefe und Kurtosis
  const skewness = n > 2 ? m3 / n / Math.pow(stdDev, 3) : 0;
  const kurtosis = n > 3 ? m4 / n / Math.pow(stdDev, 4) - 3 : 0; // Excess Kurtosis

  // SCHRITT 6: Alle Ergebnisse zusammenfassen
  return {
    mean,
    median: getPercentile(50),
    stdDev,
    variance,
    min: sorted[0],
    max: sorted[n - 1],
    p1: getPercentile(1),
    p5: getPercentile(5),
    p10: getPercentile(10),
    p25: getPercentile(25),
    p75: getPercentile(75),
    p90: getPercentile(90),
    p95: getPercentile(95),
    p99: getPercentile(99),
    count: n,
    skewness,
    kurtosis,
  };
};
